import AlertBox from '@/components/AlertBox';
import { toast } from '@/components/ui/use-toast';
import { sendProcessBillingAffidavit } from '@/services/processBillingServerService';
import { clientGetById } from '@/services/Network';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { ProcessBillingDocumentResponse } from '@/types/processBillingServer.types';
import { Checkbox } from '@/components/ui/checkbox';

export type AffidavitMode = 'AFFIDAVIT' | 'AFFIDAVIT_WITH_INVOICE';

export type AffidavitSendFn = (
  workOrderId: string,
  body: { documentIds: string[]; mode: AffidavitMode }
) => Promise<{ sentTo: string[]; cc: string[] }>;

interface SendAffidavitConfirmDialogProps {
  workOrderId: string;
  /** Affidavit document ids to send. */
  documentIds?: string[];
  /**
   * @deprecated Prefer `documentIds`. Kept so existing server-billing callers
   * that pass full `ProcessBillingDocumentResponse[]` continue to compile.
   * Ignored if `documentIds` is provided.
   */
  documents?: ProcessBillingDocumentResponse[];
  clientId: string | null;
  /**
   * Service used to actually trigger the send. Defaults to the
   * server-billing send endpoint to preserve existing behavior.
   */
  sendFn?: AffidavitSendFn;
  onClose: () => void;
}

function pickEmails(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
  if (typeof value === 'string' && value.length > 0) return [value];
  return [];
}

export const SendAffidavitConfirmDialog = ({
  workOrderId,
  documentIds,
  documents,
  clientId,
  sendFn = sendProcessBillingAffidavit,
  onClose,
}: SendAffidavitConfirmDialogProps) => {
  const [withInvoice, setWithInvoice] = useState(false);
  const clientQuery = clientGetById(clientId ?? '', { query: { enabled: !!clientId } });

  const resolvedDocumentIds = useMemo(() => {
    if (documentIds && documentIds.length > 0) return documentIds;
    return (documents ?? []).map((d) => d.processBillingDocumentId);
  }, [documentIds, documents]);

  const { recipientsTo, recipientsCc } = useMemo(() => {
    const c = clientQuery.data;
    if (!c) return { recipientsTo: [] as string[], recipientsCc: [] as string[] };
    if (withInvoice) {
      return {
        recipientsTo: pickEmails(c.affidavitWithInvoice),
        recipientsCc: pickEmails(c.affidavitWithInvoiceCc),
      };
    }
    return {
      recipientsTo: pickEmails(c.affidavit),
      recipientsCc: pickEmails(c.affidavitCc),
    };
  }, [clientQuery.data, withInvoice]);

  const sendMutation = useMutation({
    mutationFn: () =>
      sendFn(workOrderId, {
        documentIds: resolvedDocumentIds,
        mode: withInvoice ? 'AFFIDAVIT_WITH_INVOICE' : 'AFFIDAVIT',
      }),
    onSuccess: (data) => {
      toast({
        description:
          data?.sentTo?.length || data?.cc?.length
            ? `Affidavit sent to ${[...(data.sentTo ?? []), ...(data.cc ?? [])].join(', ')}`
            : 'Affidavit sent.',
      });
      onClose();
    },
    onError: (err: unknown) => {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
          : undefined;
      toast({ description: msg ?? 'Failed to send affidavit.', variant: 'destructive' });
    },
  });

  const allRecipients = [...recipientsTo, ...recipientsCc];
  const recipientLabel = clientQuery.isLoading
    ? 'loading recipients…'
    : allRecipients.length
      ? allRecipients.join(', ')
      : 'no configured recipients';

  return (
    <AlertBox
      open
      title="Are you sure?"
      description={
        <div className="flex flex-col gap-3">
          <span>
            Are you sure you want to share the affidavit with the{' '}
            <span className="text-primary font-medium">{recipientLabel}</span>?
          </span>
          <label className="flex items-center gap-2 text-sm text-primary">
            <Checkbox checked={withInvoice} onCheckedChange={(checked) => setWithInvoice(checked === true)} />
            <span>Include invoice (Affidavit with Invoice)</span>
          </label>
        </div>
      }
      cancelBtnLabel="No"
      confirmBtnLabel="Yes"
      isLoading={sendMutation.isPending || clientQuery.isLoading}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      cancelBtnClick={() => onClose()}
      confirmBtnClick={() => {
        if (resolvedDocumentIds.length === 0) {
          toast({
            description: 'No documents selected to send.',
            variant: 'destructive',
          });
          return;
        }
        if (allRecipients.length === 0) {
          toast({
            description:
              'No recipients configured for this client. Add affidavit emails in Client Data Exchange and try again.',
            variant: 'destructive',
          });
          return;
        }
        sendMutation.mutate();
      }}
    />
  );
};
